import { useRef, useState } from 'react';
import {
  apiErrorText,
  useAddPayRequestClosingMutation,
  useRemindPayRequestMutation,
  useSetPayRequestClosingNotRequiredMutation,
  type PayRequestDetail,
} from '@shared/api';
import { formatFileCount } from '@shared/lib/formatFile';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { Dialog } from '@shared/ui/Dialog';
import { TextArea } from '@shared/ui/Input';
import { MultiFileList } from '@shared/ui/MultiFileList';
import {
  CLOSING_ACCEPT,
  MAX_CLOSING_FILES,
  closingBatchProblem,
  closingFileProblem,
  closingTone,
  formatDate,
  formatDateTime,
  formatDay,
  type Tone,
} from '../model';
import { FilesList } from './FilesList';
import { FilesPick } from './FilesPick';
import { daysAgo, remindersCount } from './text';
import {
  Actions,
  ErrorText,
  FieldCaption,
  Muted,
  Notice,
  SectionCard,
  SectionHead,
  SectionTitle,
  Small,
} from './styled';

/** Сколько ошибок выбора показываем разом. */
const PROBLEMS_SHOWN = 3;

const fileKey = (f: File) => `${f.name}:${f.size}:${f.lastModified}`;

interface Props {
  detail: PayRequestDetail;
  maxFileMb: number;
}

/**
 * Закрывающие документы: что с ними, файлы, загрузка. Финансисту, Бухгалтерии
 * и руководителю отдела — «Напомнить сейчас» и «Закрывающие не нужны» /
 * «Вернуть на контроль». Напоминания по графику сервер шлёт сам.
 */
export function ClosingSection({ detail, maxFileMb }: Props) {
  const r = detail.request;
  const files = detail.files.filter((f) => f.kind === 'closing');
  const [add, { isLoading: uploading }] = useAddPayRequestClosingMutation();
  const [remind, { isLoading: reminding }] = useRemindPayRequestMutation();
  const [setNotRequired, { isLoading: savingControl }] =
    useSetPayRequestClosingNotRequiredMutation();

  const [picked, setPicked] = useState<File[]>([]);
  const [problems, setProblems] = useState<string[]>([]);
  const [progress, setProgress] = useState<number | null>(null);
  const [message, setMessage] = useState<{ tone: Exclude<Tone, 'neutral'>; text: string } | null>(
    null,
  );
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | null>(null);
  const reasonRef = useRef<HTMLTextAreaElement>(null);

  function addFiles(list: File[]) {
    setMessage(null);
    const bad: string[] = [];
    const good: File[] = [];
    for (const f of list) {
      const problem = closingFileProblem(f, maxFileMb);
      if (problem) bad.push(problem);
      else good.push(f);
    }
    setPicked((prev) => {
      const seen = new Set(prev.map(fileKey));
      return [...prev, ...good.filter((f) => !seen.has(fileKey(f)))];
    });
    setProblems(bad);
  }

  async function upload() {
    const batch = closingBatchProblem(picked);
    if (batch) {
      setProblems([batch]);
      return;
    }
    setMessage(null);
    setProgress(0);
    try {
      await add({
        id: r.id,
        files: picked,
        onProgress: (p) =>
          setProgress(
            p.phase === 'upload' && p.total > 0 ? Math.round((p.loaded / p.total) * 100) : 100,
          ),
      }).unwrap();
      setMessage({
        tone: 'success',
        text: `Приложено: ${formatFileCount(picked.length)}. Бухгалтерия увидит их в заявке.`,
      });
      setPicked([]);
      setProblems([]);
    } catch (err) {
      setMessage({ tone: 'danger', text: apiErrorText(err, 'Документы не приложены.') });
    } finally {
      setProgress(null);
    }
  }

  async function onRemind() {
    setMessage(null);
    try {
      await remind(r.id).unwrap();
      setMessage({ tone: 'success', text: 'Напомнили сотруднику — уведомление ушло.' });
    } catch (err) {
      setMessage({ tone: 'danger', text: apiErrorText(err, 'Не напомнили.') });
    }
  }

  async function confirmNotRequired() {
    const text = reason.trim();
    if (text.length < 3) {
      setReasonError('Напишите, почему закрывающие не нужны.');
      return;
    }
    try {
      await setNotRequired({ id: r.id, not_required: true, reason: text }).unwrap();
      setDialogOpen(false);
      setReason('');
      setMessage({ tone: 'success', text: 'Сняли с контроля — напоминаний больше не будет.' });
    } catch (err) {
      setReasonError(apiErrorText(err, 'Не получилось снять с контроля.'));
    }
  }

  async function backToControl() {
    setMessage(null);
    try {
      await setNotRequired({ id: r.id, not_required: false }).unwrap();
      setMessage({ tone: 'success', text: 'Закрывающие снова на контроле.' });
    } catch (err) {
      setMessage({ tone: 'danger', text: apiErrorText(err, 'Не получилось вернуть на контроль.') });
    }
  }

  // Отклонена, снята или отозвана до оплаты — закрывающих не будет, не просим их.
  const noPayment =
    r.closing === 'not_due' && ['withdrawn', 'rejected', 'removed'].includes(r.stage);
  const canUpload = r.can_add_closing && !noPayment;

  let status: string;
  switch (r.closing) {
    case 'awaiting':
      status = `Счёт оплачен ${formatDate(r.first_paid_date)}${
        r.days_since_paid != null ? ` — ${daysAgo(r.days_since_paid)}` : ''
      }. Приложите УПД, акт или счёт-фактуру.`;
      break;
    case 'provided':
      status = `Приложены ${formatDateTime(r.closing_provided_at)}. Если что-то пришло позже — добавьте.`;
      break;
    case 'not_required':
      status = `Не требуются${r.closing_not_required_reason ? `: ${r.closing_not_required_reason}` : '.'}`;
      break;
    default:
      status = noPayment
        ? 'Оплаты не было — закрывающие не понадобятся.'
        : 'Понадобятся после оплаты. Если документы уже есть — приложите сразу.';
  }

  return (
    <SectionCard as="section" aria-labelledby="pay-request-closing">
      <SectionHead>
        <SectionTitle id="pay-request-closing">Закрывающие документы</SectionTitle>
        <Badge $tone={closingTone(r.closing)}>{r.closing_label}</Badge>
      </SectionHead>
      <Muted>{status}</Muted>
      {r.closing === 'awaiting' && (
        <Small>
          Напоминания приходят сами: через 5 рабочих дней после оплаты, потом раз в неделю.{' '}
          {r.closing_reminders_sent > 0
            ? `Отправлено: ${remindersCount(r.closing_reminders_sent)}${
                r.closing_last_reminded_at
                  ? `, последнее — ${formatDay(r.closing_last_reminded_at)}`
                  : ''
              }.`
            : 'Пока не напоминали.'}
          {r.next_reminder_date && ` Следующее — ${formatDate(r.next_reminder_date)}.`}
        </Small>
      )}

      {files.length > 0 ? (
        <FilesList requestId={r.id} files={files} />
      ) : (
        canUpload && r.closing !== 'not_required' && <Small>Документов пока нет.</Small>
      )}

      {canUpload && (
        <>
          <FilesPick
            accept={CLOSING_ACCEPT}
            title="Перетащите сюда УПД, акт или счёт-фактуру"
            hint={`или нажмите, чтобы выбрать: PDF, фото или скан, Excel, Word, XML из ЭДО с подписью, ZIP — до ${MAX_CLOSING_FILES} файлов за раз, каждый до ${maxFileMb} МБ`}
            disabled={uploading}
            onFiles={addFiles}
          />
          {problems.length > 0 && (
            <ErrorText role="alert">
              {problems.slice(0, PROBLEMS_SHOWN).map((p) => (
                <div key={p}>{p}</div>
              ))}
              {problems.length > PROBLEMS_SHOWN && (
                <div>И ещё {problems.length - PROBLEMS_SHOWN} — те же причины.</div>
              )}
            </ErrorText>
          )}
          {picked.length > 0 && (
            <>
              <MultiFileList
                files={picked}
                onRemove={(i) => setPicked((prev) => prev.filter((_, idx) => idx !== i))}
              />
              <Actions>
                <Button
                  type="button"
                  $variant="primary"
                  onClick={() => void upload()}
                  disabled={uploading}
                >
                  {uploading
                    ? progress != null && progress < 100
                      ? `Загружаем… ${progress} %`
                      : 'Сохраняем…'
                    : `Приложить ${formatFileCount(picked.length)}`}
                </Button>
                <Button
                  type="button"
                  $variant="ghost"
                  onClick={() => {
                    setPicked([]);
                    setProblems([]);
                  }}
                  disabled={uploading}
                >
                  Очистить
                </Button>
              </Actions>
            </>
          )}
        </>
      )}

      {r.can_manage_closing && r.closing !== 'provided' && !noPayment && (
        <Actions>
          {r.closing === 'awaiting' && (
            <Button
              type="button"
              $variant="secondary"
              onClick={() => void onRemind()}
              disabled={reminding}
            >
              {reminding ? 'Напоминаем…' : 'Напомнить сейчас'}
            </Button>
          )}
          {r.can_waive_closing && (r.closing === 'awaiting' || r.closing === 'not_due') && (
            <Button
              type="button"
              $variant="ghost"
              onClick={() => {
                setReason('');
                setReasonError(null);
                setDialogOpen(true);
              }}
            >
              Закрывающие не нужны
            </Button>
          )}
          {r.closing === 'not_required' && (
            <Button
              type="button"
              $variant="secondary"
              onClick={() => void backToControl()}
              disabled={savingControl}
            >
              Вернуть на контроль
            </Button>
          )}
        </Actions>
      )}

      {message && (
        <Notice $tone={message.tone} role={message.tone === 'danger' ? 'alert' : 'status'}>
          {message.text}
        </Notice>
      )}

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title="Закрывающие документы не нужны?"
        initialFocusRef={reasonRef}
        footer={
          <>
            <Button type="button" $variant="secondary" onClick={() => setDialogOpen(false)}>
              Отмена
            </Button>
            <Button
              type="button"
              $variant="primary"
              onClick={() => void confirmNotRequired()}
              disabled={savingControl}
            >
              {savingControl ? 'Сохраняем…' : 'Снять с контроля'}
            </Button>
          </>
        }
      >
        <p>
          Заявка уйдёт из списка «Нет закрывающих», напоминания сотруднику прекратятся. Причину
          увидят сотрудник и бухгалтерия.
        </p>
        <FieldCaption htmlFor="pay-request-not-required">Почему не нужны</FieldCaption>
        <TextArea
          ref={reasonRef}
          id="pay-request-not-required"
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
            setReasonError(null);
          }}
          placeholder="Например: оплата госпошлины — закрывающих документов не бывает"
          rows={3}
          maxLength={500}
          aria-invalid={reasonError ? true : undefined}
          aria-describedby={reasonError ? 'pay-request-not-required-error' : undefined}
        />
        {reasonError && (
          <ErrorText id="pay-request-not-required-error" role="alert">
            {reasonError}
          </ErrorText>
        )}
      </Dialog>
    </SectionCard>
  );
}
